import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc, setDoc, addDoc, updateDoc, collection, query, where, getDocs } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { signOut } from "firebase/auth";
import { auth, db, storage } from "./firebase";
import { germanStates, stateList } from "./germanyData";
import { useLanguage } from "./LanguageContext";
import NotificationBell from "./NotificationBell";
import { sendNotification } from "./notifications";

function PatientDashboard({ userData }) {
  const [activeTab, setActiveTab] = useState("home");
  const [bookingFilter, setBookingFilter] = useState("booked");
  const [insuranceNumber, setInsuranceNumber] = useState("");
  const [mobile, setMobile] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [photoLocked, setPhotoLocked] = useState(false);
  const [newPhoto, setNewPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [providers, setProviders] = useState([]);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [stateFilter, setStateFilter] = useState("all");
  const [cityFilter, setCityFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [searchDate, setSearchDate] = useState("");
  const [availabilityMap, setAvailabilityMap] = useState({});
  const [ratingMap, setRatingMap] = useState({});
  const [myBookings, setMyBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [contactMap, setContactMap] = useState({});
  const [myReviewedBookingIds, setMyReviewedBookingIds] = useState([]);
  const [reviewingBookingId, setReviewingBookingId] = useState(null);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [requestingProvider, setRequestingProvider] = useState(null);
  const [reqDate, setReqDate] = useState("");
  const [reqTime, setReqTime] = useState("");
  const [reqHospital, setReqHospital] = useState("");
  const [reqType, setReqType] = useState("pickup_dropoff");
  const [reqNotes, setReqNotes] = useState("");
  const [reqSubmitting, setReqSubmitting] = useState(false);
  const navigate = useNavigate();
  const { lang, toggleLang, t } = useLanguage();

  useEffect(() => {
    const loadProfile = async () => {
      const user = auth.currentUser;
      if (!user) return;
      const snap = await getDoc(doc(db, "patientProfiles", user.uid));
      if (snap.exists()) {
        const data = snap.data();
        setInsuranceNumber(data.insuranceNumber || "");
        setMobile(data.mobile || "");
        setPhotoUrl(data.photoUrl || "");
        setPhotoLocked(!!data.photoUrl);
      }
    };
    loadProfile();
  }, []);

  useEffect(() => {
    if (activeTab === "search") {
      loadProviders();
    }
    if (activeTab === "bookings") {
      loadBookings();
      loadMyReviews();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "search" && searchDate && providers.length > 0) {
      loadAvailability();
    }
    if (activeTab === "search" && providers.length > 0) {
      loadRatings();
    }
  }, [searchDate, providers, activeTab]);

  const loadProviders = async () => {
    setLoadingProviders(true);
    try {
      const q = query(collection(db, "providers"), where("verificationStatus", "==", "verified"));
      const snap = await getDocs(q);
      const results = [];
      snap.forEach((d) => results.push(d.data()));
      setProviders(results);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingProviders(false);
    }
  };

  const loadRatings = async () => {
    const newMap = {};
    for (const p of providers) {
      const q = query(collection(db, "reviews"), where("providerId", "==", p.uid));
      const snap = await getDocs(q);
      let total = 0;
      let count = 0;
      snap.forEach((d) => {
        total += d.data().rating;
        count += 1;
      });
      if (count > 0) {
        newMap[p.uid] = { avg: (total / count).toFixed(1), count: count };
      }
    }
    setRatingMap(newMap);
  };

  const loadAvailability = async () => {
    const newMap = {};
    for (const p of providers) {
      if (p.role === "individual") {
        const q = query(
          collection(db, "bookings"),
          where("providerId", "==", p.uid),
          where("status", "==", "confirmed"),
          where("date", "==", searchDate)
        );
        const snap = await getDocs(q);
        newMap[p.uid] = { type: "individual", available: snap.empty };
      } else if (p.role === "agency") {
        const empQ = query(collection(db, "employees"), where("agencyId", "==", p.uid));
        const empSnap = await getDocs(empQ);
        const allEmployees = [];
        empSnap.forEach((d) => allEmployees.push({ id: d.id, ...d.data() }));

        const bookingQ = query(
          collection(db, "bookings"),
          where("providerId", "==", p.uid),
          where("status", "==", "confirmed"),
          where("date", "==", searchDate)
        );
        const bookingSnap = await getDocs(bookingQ);
        const assignedEmployeeIds = [];
        bookingSnap.forEach((d) => {
          if (d.data().employeeId) assignedEmployeeIds.push(d.data().employeeId);
        });

        const freeCount = allEmployees.filter((emp) => {
          const isUnavailable = emp.unavailableDates && emp.unavailableDates.includes(searchDate);
          const isAssigned = assignedEmployeeIds.includes(emp.id);
          return !isUnavailable && !isAssigned;
        }).length;

        newMap[p.uid] = { type: "agency", freeCount: freeCount, totalCount: allEmployees.length };
      }
    }
    setAvailabilityMap(newMap);
  };

  const loadBookings = async () => {
    setLoadingBookings(true);
    try {
      const user = auth.currentUser;
      const q = query(collection(db, "bookings"), where("patientId", "==", user.uid));
      const snap = await getDocs(q);
      const results = [];
      snap.forEach((d) => results.push({ id: d.id, ...d.data() }));
      setMyBookings(results);
      loadContacts(results);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingBookings(false);
    }
  };

  const loadMyReviews = async () => {
    try {
      const user = auth.currentUser;
      const q = query(collection(db, "reviews"), where("patientId", "==", user.uid));
      const snap = await getDocs(q);
      const ids = [];
      snap.forEach((d) => ids.push(d.data().bookingId));
      setMyReviewedBookingIds(ids);
    } catch (err) {
      console.error(err);
    }
  };

  const loadContacts = async (bookingsList) => {
    const newMap = {};
    for (const b of bookingsList) {
      if (b.status !== "confirmed" && b.status !== "completed") continue;
      if (b.employeeId) {
        const snap = await getDoc(doc(db, "employees", b.employeeId));
        if (snap.exists()) {
          newMap[b.id] = { name: snap.data().name, phone: snap.data().phone, qualification: snap.data().qualification };
        }
      } else if (b.providerRole === "individual") {
        const snap = await getDoc(doc(db, "providers", b.providerId));
        if (snap.exists()) {
          newMap[b.id] = { name: "", phone: snap.data().phone, qualification: snap.data().qualification };
        }
      }
    }
    setContactMap(newMap);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      const user = auth.currentUser;
      let uploadedUrl = photoUrl;

      if (newPhoto && !photoLocked) {
        const filePath = "patient-photos/" + user.uid + "/photo.jpg";
        const fileRef = ref(storage, filePath);
        await uploadBytes(fileRef, newPhoto);
        uploadedUrl = await getDownloadURL(fileRef);
      }

      await setDoc(doc(db, "patientProfiles", user.uid), {
        uid: user.uid,
        insuranceNumber: insuranceNumber,
        mobile: mobile,
        photoUrl: uploadedUrl,
        updatedAt: new Date().toISOString(),
      }, { merge: true });

      setPhotoUrl(uploadedUrl);
      if (uploadedUrl) setPhotoLocked(true);
      setMessage(t("saveProfile") + " OK");
    } catch (err) {
      setMessage("Error: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  const openRequestForm = (provider) => {
    setRequestingProvider(provider);
    setReqDate(searchDate || "");
    setReqTime("");
    setReqHospital("");
    setReqType("pickup_dropoff");
    setReqNotes("");
  };

  const submitRequest = async (e) => {
    e.preventDefault();
    setReqSubmitting(true);
    try {
      const user = auth.currentUser;
      await addDoc(collection(db, "bookings"), {
        patientId: user.uid,
        patientName: userData.name,
        providerId: requestingProvider.uid,
        providerRole: requestingProvider.role,
        date: reqDate,
        time: reqTime,
        hospital: reqHospital,
        supportType: reqType,
        notes: reqNotes,
        status: "requested",
        createdAt: new Date().toISOString(),
      });
      await sendNotification(
        requestingProvider.uid,
        "Neue Anfrage von " + userData.name + " fur " + reqDate,
        "New request from " + userData.name + " for " + reqDate
      );
      setRequestingProvider(null);
      setActiveTab("bookings");
      setBookingFilter("booked");
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setReqSubmitting(false);
    }
  };

  const cancelBooking = async (booking) => {
    if (!window.confirm(lang === "de" ? "Termin wirklich stornieren?" : "Really cancel this appointment?")) return;
    try {
      await updateDoc(doc(db, "bookings", booking.id), { status: "cancelled" });
      const notifyId = booking.employeeId || booking.providerId;
      if (notifyId) {
        await sendNotification(
          notifyId,
          userData.name + " hat den Termin am " + booking.date + " storniert.",
          userData.name + " cancelled the appointment on " + booking.date + "."
        );
      }
      loadBookings();
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  const openReviewForm = (bookingId) => {
    setReviewingBookingId(bookingId);
    setReviewRating(5);
    setReviewComment("");
  };

  const submitReview = async (booking) => {
    setSubmittingReview(true);
    try {
      const user = auth.currentUser;
      await addDoc(collection(db, "reviews"), {
        patientId: user.uid,
        providerId: booking.providerId,
        bookingId: booking.id,
        rating: reviewRating,
        comment: reviewComment,
        createdAt: new Date().toISOString(),
      });
      setReviewingBookingId(null);
      loadMyReviews();
    } catch (err) {
      alert("Error: " + err.message);
    } finally {
      setSubmittingReview(false);
    }
  };

  const profileComplete = insuranceNumber && mobile;

  const handleStateFilterChange = (val) => {
    setStateFilter(val);
    setCityFilter("all");
  };

  const filteredProviders = providers.filter((p) => {
    const stateMatch = stateFilter === "all" || p.state === stateFilter;
    const cityMatch = cityFilter === "all" || p.city === cityFilter;
    const typeMatch = typeFilter === "all" || p.role === typeFilter;
    return stateMatch && cityMatch && typeMatch;
  });

  const qualificationLabel = (q) => {
    const map = {
      nursing_student: lang === "de" ? "Pflege-/Medizinstudent" : "Nursing/Medical Student",
      retired_nurse: lang === "de" ? "Ehemalige Pflegekraft" : "Retired Nurse",
      alltagsbegleiter: lang === "de" ? "Zertifizierter Alltagsbegleiter" : "Certified Companion",
      other: lang === "de" ? "Sonstige" : "Other",
      agency: lang === "de" ? "Agentur" : "Agency",
    };
    return map[q] || q;
  };

  const bookingsForTab = myBookings.filter((b) => {
    if (bookingFilter === "booked") return b.status === "requested";
    if (bookingFilter === "confirmed") return b.status === "confirmed";
    if (bookingFilter === "completed") return b.status === "completed";
    return false;
  });

  const starDisplay = (n) => "*".repeat(n) + "-".repeat(5 - n);

  return (
    <div style={styles.pageBackground}>
      <div style={styles.phoneFrame}>
        <div style={styles.header}>
          <div>
            <div style={styles.headerTitle}>{t("welcome")}</div>
            <div style={styles.headerSubtitle}>{userData.name}</div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <NotificationBell />
            <button onClick={toggleLang} style={styles.langToggle}>
              {lang === "de" ? "EN" : "DE"}
            </button>
            <button onClick={handleLogout} style={styles.logoutBtn}>{t("logout")}</button>
          </div>
        </div>

        <div style={styles.content}>
          {activeTab === "home" && (
            <div>
              {!profileComplete && (
                <div style={styles.noticeCard}>
                  <div style={styles.noticeIcon}>!</div>
                  <div>
                    <div style={styles.noticeTitle}>{t("incompleteProfile")}</div>
                    <div style={styles.noticeText}>{t("completeProfilePrompt")}</div>
                  </div>
                </div>
              )}

              <div style={styles.sectionLabel}>{t("overview")}</div>
              <div style={styles.grid}>
                <div style={styles.statCard}>
                  <div style={styles.statNumber}>{myBookings.filter(b => b.status === "requested").length}</div>
                  <div style={styles.statLabel}>{t("bookedAppointments")}</div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statNumber}>{myBookings.filter(b => b.status === "confirmed").length}</div>
                  <div style={styles.statLabel}>{t("confirmedAppointments")}</div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statNumber}>{myBookings.filter(b => b.status === "completed").length}</div>
                  <div style={styles.statLabel}>{t("completed")}</div>
                </div>
                <div style={styles.statCard}>
                  <div style={styles.statNumber}>{profileComplete ? "OK" : "-"}</div>
                  <div style={styles.statLabel}>{t("profileStatus")}</div>
                </div>
              </div>

              <button onClick={() => setActiveTab("search")} style={styles.button}>
                {t("findCompanion")}
              </button>
            </div>
          )}

          {activeTab === "search" && !requestingProvider && (
            <div>
              <div style={styles.field}>
                <label style={styles.label}>{lang === "de" ? "Datum (fur Verfugbarkeit)" : "Date (for availability)"}</label>
                <input type="date" value={searchDate} onChange={(e) => setSearchDate(e.target.value)} style={styles.input} />
              </div>

              <div style={styles.filterRow}>
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={styles.filterSelect}>
                  <option value="all">{t("allTypes")}</option>
                  <option value="individual">{t("individual")}</option>
                  <option value="agency">{t("agency")}</option>
                </select>
              </div>
              <div style={styles.filterRow}>
                <select value={stateFilter} onChange={(e) => handleStateFilterChange(e.target.value)} style={styles.filterSelect}>
                  <option value="all">{t("allStates")}</option>
                  {stateList.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} style={styles.filterSelect} disabled={stateFilter === "all"}>
                  <option value="all">{t("allCities")}</option>
                  {(stateFilter === "all" ? [] : germanStates[stateFilter]).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {loadingProviders && <p style={{ textAlign: "center", color: "#999" }}>{t("loading")}</p>}

              {!loadingProviders && filteredProviders.length === 0 && (
                <div style={styles.emptyState}>
                  <p>{t("noProvidersFound")}</p>
                </div>
              )}

              {filteredProviders.map((p) => {
                const avail = availabilityMap[p.uid];
                const rating = ratingMap[p.uid];
                return (
                  <div key={p.uid} style={styles.providerCard}>
                    <div style={styles.providerHeader}>
                      <div style={styles.providerAvatar}>{p.role === "agency" ? "A" : "E"}</div>
                      <div>
                        <div style={styles.providerType}>
                          {p.role === "agency" ? t("agency") : qualificationLabel(p.qualification)}
                        </div>
                        <div style={styles.providerArea}>{p.city}, {p.state}</div>
                      </div>
                    </div>

                    {rating && (
                      <div style={styles.ratingBadge}>
                        {starDisplay(Math.round(rating.avg))} {rating.avg} ({rating.count})
                      </div>
                    )}

                    <p style={styles.providerBio}>{p.bio}</p>

                    {searchDate && avail && avail.type === "individual" && (
                      <div style={avail.available ? styles.availableBadge : styles.unavailableBadge}>
                        {avail.available
                          ? (lang === "de" ? "Verfugbar am " + searchDate : "Available on " + searchDate)
                          : (lang === "de" ? "Nicht verfugbar am " + searchDate : "Not available on " + searchDate)}
                      </div>
                    )}

                    {searchDate && avail && avail.type === "agency" && (
                      <div style={avail.freeCount > 0 ? styles.availableBadge : styles.unavailableBadge}>
                        {avail.freeCount} {lang === "de" ? "von" : "of"} {avail.totalCount} {lang === "de" ? "Mitarbeitern frei am " + searchDate : "employees free on " + searchDate}
                      </div>
                    )}

                    <button style={styles.requestButton} onClick={() => openRequestForm(p)}>{t("request")}</button>
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === "search" && requestingProvider && (
            <form onSubmit={submitRequest}>
              <button type="button" onClick={() => setRequestingProvider(null)} style={styles.backLink}>
                <span>&#8592;</span> {lang === "de" ? "Zuruck" : "Back"}
              </button>
              <div style={styles.sectionLabel}>
                {lang === "de" ? "Anfrage senden" : "Send Request"}
              </div>

              <div style={styles.field}>
                <label style={styles.label}>{lang === "de" ? "Datum" : "Date"}</label>
                <input type="date" value={reqDate} onChange={(e) => setReqDate(e.target.value)} required style={styles.input} />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>{lang === "de" ? "Uhrzeit" : "Time"}</label>
                <input type="time" value={reqTime} onChange={(e) => setReqTime(e.target.value)} required style={styles.input} />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>{lang === "de" ? "Klinik / Krankenhaus" : "Clinic / Hospital"}</label>
                <input value={reqHospital} onChange={(e) => setReqHospital(e.target.value)} required style={styles.input} />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>{lang === "de" ? "Art der Unterstutzung" : "Type of Support"}</label>
                <select value={reqType} onChange={(e) => setReqType(e.target.value)} style={styles.input}>
                  <option value="pickup_dropoff">{lang === "de" ? "Abholung, Begleitung und Nachhausebringen" : "Pickup, escort and drop-off"}</option>
                  <option value="meet_only">{lang === "de" ? "Nur Treffen in der Klinik" : "Meet at hospital only"}</option>
                </select>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>{lang === "de" ? "Notizen" : "Notes"}</label>
                <textarea value={reqNotes} onChange={(e) => setReqNotes(e.target.value)} rows={3} style={styles.input} />
              </div>

              <button type="submit" disabled={reqSubmitting} style={styles.button}>
                {reqSubmitting ? t("saving") : (lang === "de" ? "Anfrage senden" : "Send Request")}
              </button>
            </form>
          )}

          {activeTab === "bookings" && (
            <div>
              <div style={styles.pillRow}>
                <button
                  onClick={() => setBookingFilter("booked")}
                  style={bookingFilter === "booked" ? styles.pillActive : styles.pill}
                >
                  {t("booked")}
                </button>
                <button
                  onClick={() => setBookingFilter("confirmed")}
                  style={bookingFilter === "confirmed" ? styles.pillActive : styles.pill}
                >
                  {t("confirmed")}
                </button>
                <button
                  onClick={() => setBookingFilter("completed")}
                  style={bookingFilter === "completed" ? styles.pillActive : styles.pill}
                >
                  {t("completed")}
                </button>
              </div>

              {loadingBookings && <p style={{ textAlign: "center", color: "#999" }}>{t("loading")}</p>}

              {!loadingBookings && bookingsForTab.length === 0 && (
                <div style={styles.emptyState}>
                  <p>
                    {bookingFilter === "booked" && t("noBookedAppointments")}
                    {bookingFilter === "confirmed" && t("noConfirmedAppointments")}
                    {bookingFilter === "completed" && t("noCompletedAppointments")}
                  </p>
                </div>
              )}

              {bookingsForTab.map((b) => {
                const contact = contactMap[b.id];
                const alreadyReviewed = myReviewedBookingIds.includes(b.id);
                return (
                  <div key={b.id} style={styles.providerCard}>
                    <div style={styles.providerType}>{b.hospital}</div>
                    <div style={styles.providerArea}>{b.date} - {b.time}</div>
                    {b.employeeName && <div style={styles.providerArea}>{lang === "de" ? "Mitarbeiter" : "Employee"}: {b.employeeName}</div>}
                    <p style={styles.providerBio}>{b.notes}</p>

                    {contact && (
                      <div style={styles.contactBox}>
                        <div style={{ fontWeight: 700, fontSize: 12 }}>
                          {lang === "de" ? "Kontakt fur diesen Termin" : "Contact for this appointment"}
                        </div>
                        {contact.qualification && (
                          <div style={{ fontSize: 12, color: "#555" }}>
                            {qualificationLabel(contact.qualification)}
                          </div>
                        )}
                        {contact.phone && (
                          <div style={{ fontSize: 12, color: "#555" }}>{contact.phone}</div>
                        )}
                      </div>
                    )}

                    {(bookingFilter === "booked" || bookingFilter === "confirmed") && (
                      <button onClick={() => cancelBooking(b)} style={styles.cancelButton}>
                        {lang === "de" ? "Stornieren" : "Cancel"}
                      </button>
                    )}

                    {bookingFilter === "completed" && !alreadyReviewed && reviewingBookingId !== b.id && (
                      <button onClick={() => openReviewForm(b.id)} style={styles.requestButton}>
                        {lang === "de" ? "Bewerten" : "Rate"}
                      </button>
                    )}

                    {bookingFilter === "completed" && alreadyReviewed && (
                      <div style={styles.reviewedBadge}>
                        {lang === "de" ? "Bereits bewertet" : "Already rated"}
                      </div>
                    )}

                    {reviewingBookingId === b.id && (
                      <div style={{ marginTop: 10 }}>
                        <label style={styles.label}>{lang === "de" ? "Bewertung" : "Rating"}</label>
                        <select value={reviewRating} onChange={(e) => setReviewRating(Number(e.target.value))} style={styles.input}>
                          <option value={5}>5 - {lang === "de" ? "Ausgezeichnet" : "Excellent"}</option>
                          <option value={4}>4 - {lang === "de" ? "Gut" : "Good"}</option>
                          <option value={3}>3 - {lang === "de" ? "Okay" : "Okay"}</option>
                          <option value={2}>2 - {lang === "de" ? "Schlecht" : "Poor"}</option>
                          <option value={1}>1 - {lang === "de" ? "Sehr schlecht" : "Very Poor"}</option>
                        </select>
                        <label style={styles.label}>{lang === "de" ? "Kommentar (optional)" : "Comment (optional)"}</label>
                        <textarea value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} rows={2} style={styles.input} />
                        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                          <button onClick={() => submitReview(b)} disabled={submittingReview} style={styles.requestButton}>
                            {submittingReview ? t("saving") : (lang === "de" ? "Absenden" : "Submit")}
                          </button>
                          <button onClick={() => setReviewingBookingId(null)} style={styles.cancelButton}>
                            {lang === "de" ? "Abbrechen" : "Cancel"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {activeTab === "profile" && (
            <form onSubmit={handleSaveProfile}>
              <div style={styles.photoSection}>
                {photoUrl ? (
                  <img src={photoUrl} alt="Profil" style={styles.photo} />
                ) : (
                  <div style={styles.photoPlaceholder}>{t("noPhoto")}</div>
                )}
                {!photoLocked ? (
                  <div style={{ marginTop: 10 }}>
                    <input type="file" accept="image/*" onChange={(e) => setNewPhoto(e.target.files[0])} />
                    <p style={styles.hint}>{t("photoOneTime")}</p>
                  </div>
                ) : (
                  <p style={styles.hint}>{t("photoLocked")}</p>
                )}
              </div>

              <div style={styles.field}>
                <label style={styles.label}>{t("name")}</label>
                <input value={userData.name} disabled style={{ ...styles.input, background: "#f5f5f5" }} />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>{t("email")}</label>
                <input value={userData.email} disabled style={{ ...styles.input, background: "#f5f5f5" }} />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>{t("insuranceNumber")}</label>
                <input value={insuranceNumber} onChange={(e) => setInsuranceNumber(e.target.value)} style={styles.input} />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>{t("mobileNumber")}</label>
                <input value={mobile} onChange={(e) => setMobile(e.target.value)} style={styles.input} />
              </div>

              {message && <p style={styles.message}>{message}</p>}

              <button type="submit" disabled={saving} style={styles.button}>
                {saving ? t("saving") : t("saveProfile")}
              </button>
            </form>
          )}
        </div>

        <div style={styles.bottomNav}>
          <button onClick={() => { setActiveTab("home"); setRequestingProvider(null); }} style={activeTab === "home" ? styles.navItemActive : styles.navItem}>
            <div style={styles.navIcon}>H</div>
            <div style={styles.navLabel}>{t("home")}</div>
          </button>
          <button onClick={() => { setActiveTab("search"); setRequestingProvider(null); }} style={activeTab === "search" ? styles.navItemActive : styles.navItem}>
            <div style={styles.navIcon}>S</div>
            <div style={styles.navLabel}>{t("search")}</div>
          </button>
          <button onClick={() => { setActiveTab("bookings"); setRequestingProvider(null); }} style={activeTab === "bookings" ? styles.navItemActive : styles.navItem}>
            <div style={styles.navIcon}>B</div>
            <div style={styles.navLabel}>{t("bookings")}</div>
          </button>
          <button onClick={() => { setActiveTab("profile"); setRequestingProvider(null); }} style={activeTab === "profile" ? styles.navItemActive : styles.navItem}>
            <div style={styles.navIcon}>P</div>
            <div style={styles.navLabel}>{t("profile")}</div>
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  pageBackground: {
    minHeight: "100vh",
    background: "#dce6f2",
    display: "flex",
    justifyContent: "center",
    fontFamily: "'Segoe UI', sans-serif",
  },
  phoneFrame: {
    width: "100%",
    maxWidth: 420,
    minHeight: "100vh",
    background: "#f5f8fc",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 0 40px rgba(0,0,0,0.12)",
  },
  header: {
    background: "linear-gradient(135deg, #1a5fb4, #2f7fd6)",
    color: "#fff",
    padding: "20px 22px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerTitle: { fontSize: 13, opacity: 0.85 },
  headerSubtitle: { fontSize: 19, fontWeight: 700, marginTop: 2 },
  langToggle: {
    background: "rgba(255,255,255,0.2)",
    color: "#fff",
    border: "none",
    padding: "8px 10px",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  logoutBtn: {
    background: "rgba(255,255,255,0.2)",
    color: "#fff",
    border: "none",
    padding: "8px 14px",
    borderRadius: 8,
    fontSize: 13,
    cursor: "pointer",
  },
  content: {
    flex: 1,
    padding: "18px",
    paddingBottom: 100,
    boxSizing: "border-box",
  },
  noticeCard: {
    background: "#fff4e5",
    border: "1px solid #ffd591",
    borderRadius: 12,
    padding: 14,
    display: "flex",
    gap: 10,
    marginBottom: 18,
  },
  noticeIcon: { fontSize: 20, fontWeight: 700, color: "#874d00" },
  noticeTitle: { fontWeight: 700, fontSize: 13, color: "#874d00" },
  noticeText: { fontSize: 12, color: "#874d00", marginTop: 2 },
  sectionLabel: { fontSize: 12, color: "#888", fontWeight: 700, letterSpacing: 0.5, marginBottom: 10 },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    marginBottom: 18,
  },
  statCard: {
    background: "#fff",
    borderRadius: 14,
    padding: 16,
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
  },
  statNumber: { fontSize: 24, fontWeight: 700, color: "#1a5fb4" },
  statLabel: { fontSize: 11, color: "#777", marginTop: 4 },
  field: { marginBottom: 14 },
  label: { display: "block", fontSize: 12, color: "#333", marginBottom: 6, fontWeight: 600 },
  input: {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 8,
    border: "1px solid #ccc",
    fontSize: 13,
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
  photoSection: { textAlign: "center", marginBottom: 18 },
  photo: { width: 90, height: 90, borderRadius: "50%", objectFit: "cover", border: "2px solid #1a5fb4" },
  photoPlaceholder: {
    width: 90, height: 90, borderRadius: "50%", background: "#eee",
    display: "flex", alignItems: "center", justifyContent: "center",
    margin: "0 auto", color: "#999", fontSize: 11,
  },
  hint: { fontSize: 11, color: "#777", marginTop: 8 },
  message: { fontSize: 12, color: "#1a5fb4", marginBottom: 8 },
  button: {
    width: "100%",
    padding: "12px",
    background: "#1a5fb4",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    marginTop: 6,
  },
  backLink: {
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: 10,
    color: "#1a5fb4",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    marginTop: 6,
    marginBottom: 14,
    padding: "8px 14px",
    display: "flex",
    alignItems: "center",
    gap: 6,
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  cancelButton: {
    width: "100%",
    padding: "8px",
    background: "#fde8e8",
    color: "#c62828",
    border: "none",
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
    marginTop: 8,
  },
  contactBox: {
    background: "#f0f6ff",
    borderRadius: 8,
    padding: "8px 12px",
    marginTop: 8,
    marginBottom: 8,
  },
  emptyState: { textAlign: "center", color: "#999", padding: "50px 0" },
  bottomNav: {
    background: "#fff",
    borderTop: "1px solid #eee",
    display: "flex",
    justifyContent: "space-around",
    padding: "10px 0",
  },
  navItem: {
    background: "none",
    border: "none",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    color: "#999",
    cursor: "pointer",
    fontSize: 11,
  },
  navItemActive: {
    background: "none",
    border: "none",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 2,
    color: "#1a5fb4",
    cursor: "pointer",
    fontSize: 11,
    fontWeight: 700,
  },
  navIcon: {
    fontSize: 14,
    fontWeight: 700,
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: "#eee",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  navLabel: { fontSize: 10 },
  pillRow: { display: "flex", gap: 8, marginBottom: 18 },
  pill: {
    flex: 1,
    padding: "8px 6px",
    borderRadius: 20,
    border: "1px solid #ddd",
    background: "#fff",
    color: "#555",
    fontSize: 12,
    cursor: "pointer",
  },
  pillActive: {
    flex: 1,
    padding: "8px 6px",
    borderRadius: 20,
    border: "1px solid #1a5fb4",
    background: "#1a5fb4",
    color: "#fff",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
  filterRow: { display: "flex", gap: 8, marginBottom: 10 },
  filterSelect: {
    flex: 1,
    padding: "8px",
    borderRadius: 8,
    border: "1px solid #ccc",
    fontSize: 12,
  },
  providerCard: {
    background: "#fff",
    borderRadius: 14,
    padding: 16,
    marginBottom: 12,
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
  },
  providerHeader: { display: "flex", alignItems: "center", gap: 10, marginBottom: 8 },
  providerAvatar: {
    width: 36, height: 36, borderRadius: "50%", background: "#1a5fb4",
    color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
    fontWeight: 700, fontSize: 14,
  },
  providerType: { fontWeight: 700, fontSize: 13, color: "#222" },
  providerArea: { fontSize: 12, color: "#888" },
  providerBio: { fontSize: 13, color: "#555", marginBottom: 10 },
  requestButton: {
    width: "100%",
    padding: "8px",
    background: "#1a5fb4",
    color: "#fff",
    border: "none",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  availableBadge: {
    background: "#e8f5e9",
    color: "#256029",
    padding: "6px 10px",
    borderRadius: 8,
    fontSize: 11,
    fontWeight: 600,
    marginBottom: 8,
    display: "inline-block",
  },
  unavailableBadge: {
    background: "#fde8e8",
    color: "#c62828",
    padding: "6px 10px",
    borderRadius: 8,
    fontSize: 11,
    fontWeight: 600,
    marginBottom: 8,
    display: "inline-block",
  },
  ratingBadge: {
    background: "#fff8e1",
    color: "#8a6d00",
    padding: "4px 10px",
    borderRadius: 8,
    fontSize: 11,
    fontWeight: 600,
    marginBottom: 8,
    display: "inline-block",
  },
  reviewedBadge: {
    textAlign: "center",
    color: "#999",
    fontSize: 12,
    marginTop: 6,
  },
};

export default PatientDashboard;

